import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAppContext } from './AppContext';

interface ThemeContextType {
  isDark: boolean;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  currentTheme: 'light' | 'dark' | 'system';
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { state, dispatch } = useAppContext();
  const { theme } = state.preferences;

  // Determine if dark mode should be active
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const setTheme = (newTheme: 'light' | 'dark' | 'system') => {
    dispatch({ type: 'SET_THEME', payload: newTheme });
    // Theme persistence is now handled automatically by AppContext
  };

  // Apply theme to document root
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        // Trigger re-render by updating state (will cause isDark to recalculate)
        dispatch({ type: 'SET_THEME', payload: 'system' });
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme, dispatch]);

  return (
    <ThemeContext.Provider value={{ isDark, setTheme, currentTheme: theme }}>
      {children}
    </ThemeContext.Provider>
  );
};